/**
 * Service wrappers for the v2 provisional-forecast endpoints.
 *
 * Wraps the same axios helpers (`getRequest` / `postRequest`) the rest
 * of fdp_app uses so authentication, baseURL, CSRF, and error shape
 * stay consistent. The corresponding TypeScript contracts live in
 * `Interfaces/FDPInterfaces/provisionalForecastV2Interfaces.ts`.
 */

import { getRequest, postRequest } from 'config/Axios/baseConfig';
import {
  CreateTempNDCRequest,
  CreateTempNDCResponse,
  GetAllNDCsForGFCResponse,
  GetNDC11NotesForNDCsRequest,
  GetNDC11NotesForNDCsResponse,
  GetProvisionalForecastForNDCsRequest,
  GetProvisionalForecastForNDCsResponse,
  PreLaunchConceptSuggestionsResponse,
  ProvisionalForecastForNDCResponse,
  SaveNDC11NoteRequest,
  SaveNDC11NoteResponse,
  SaveProvisionalForecastForNDCRequest,
  SaveProvisionalForecastForNDCResponse,
  SaveProvisionalForecastForNDCsRequest,
  SaveProvisionalForecastForNDCsResponse,
} from 'Interfaces/FDPInterfaces/provisionalForecastV2Interfaces';
import { conceptDetailsInterface, notesInterface } from 'Interfaces/FDPInterfaces/tmsInterfaces';

export const provisionalForecastV2Services = {
  /**
   * GET /fdp/get_concept_details_for_gfc/<gfc>
   *
   * Concept-details payload for the v2 Edit screen's ConceptDetailsSection.
   * Mirrors the legacy `fdp/get_concept_details/<gfc>` response shape, but
   * sources concept data from the union of PreLaunchProducts and
   * GSMSConcepts with per-field merge. Each `concept_info[]` entry now
   * carries an `origin` tag (`"pre_launch"` | `"gsms"` | `"both"`).
   * `pharmacologic_class` and `duration_condition` are filled whenever
   * GSMS has the GFC; otherwise null (PreLaunch has no equivalent column).
   * `contract_info` is unchanged (still sourced from MasterContractDetails
   * server-side). See CONCEPT_DETAILS_FOR_GFC_API.md.
   *
   * TS method name is intentionally kept as `getPreLaunchConceptDetails`
   * (minimal rename) so existing callers don't need to be touched.
   */
  getPreLaunchConceptDetails: (gfc: number | string): Promise<conceptDetailsInterface> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<conceptDetailsInterface>(
          `fdp/get_concept_details_for_gfc/${gfc}`
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getPreLaunchConceptDetails, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * POST /fdp/get_concept_suggestions
   *
   * Suggestive search over the union of PreLaunchProducts and
   * GSMSConcepts. Empty/whitespace query returns the cached top-10
   * alphabetical default. Non-empty query returns up to 20 best-match
   * {GFC, Concept, origin} dicts, where `origin` is "pre_launch" or
   * "gsms". The TS type name is unchanged from the legacy pre-launch-
   * only flow because the response is a superset (the new `origin`
   * field is optional on the interface, additive on the wire).
   *
   * Accepts an optional AbortSignal so the caller can cancel an
   * in-flight request when the query changes. axios surfaces a
   * `CanceledError` (name `'CanceledError'` or `'AbortError'`) on
   * abort — the store filters those out of its error logging.
   */
  getPreLaunchConceptSuggestions: (
    query: string,
    signal?: AbortSignal
  ): Promise<PreLaunchConceptSuggestionsResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<PreLaunchConceptSuggestionsResponse>(
          'fdp/get_concept_suggestions',
          { query },
          undefined,
          signal ? { signal } : undefined
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getPreLaunchConceptSuggestions, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * GET /fdp/get_all_ndcs_for_gfc/<gfc>
   *
   * Returns every active real NDC plus every active temp packout NDC
   * for the GFC, in two separate arrays.
   *
   * Accepts an optional AbortSignal so the sidebar can cancel the
   * previous concept's load when the user clicks a new concept before
   * the first response lands — prevents late responses from
   * overwriting the freshly-selected concept's data.
   */
  getAllNDCsForGFC: (
    gfc: number | string,
    signal?: AbortSignal
  ): Promise<GetAllNDCsForGFCResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<GetAllNDCsForGFCResponse>(
          `fdp/get_all_ndcs_for_gfc/${gfc}`,
          undefined,
          signal ? { signal } : undefined
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getAllNDCsForGFC, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * POST /fdp/create_temporary_packout_ndc
   *
   * Creates a TemporaryPackoutNDC for the given GFC. Backend rejects
   * with 404 if GFC is not in PreLaunchProducts, 409 if the GFC has
   * any active real NDCs or any other active temp NDC.
   */
  createTemporaryPackoutNDC: (payload: CreateTempNDCRequest): Promise<CreateTempNDCResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<CreateTempNDCResponse>(
          'fdp/create_temporary_packout_ndc',
          payload
        );
        resolve(data);
      } catch (error) {
        console.log('in services > createTemporaryPackoutNDC, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * GET /fdp/get_provisional_forecast_for_ndc/<ndc>
   *
   * Returns the provisional forecast for a single NDC (real or temp),
   * alongside AnnualMarketSales for the GFC. Picks one of three
   * branches server-side: Case A (current PP rows), Case B (roll-forward
   * from previous PP), or Case C (synthesise fresh).
   */
  getProvisionalForecastForNDC: (ndc: string): Promise<ProvisionalForecastForNDCResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<ProvisionalForecastForNDCResponse>(
          `fdp/get_provisional_forecast_for_ndc/${encodeURIComponent(ndc)}`
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getProvisionalForecastForNDC, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * POST /fdp/save_provisional_forecast_for_ndc
   *
   * Persists the provisional forecast for a single NDC at the current
   * planning period. Backend soft-deletes any existing IsActive=True
   * rows for (NDC, current_pp) and inserts the new payload in a single
   * transaction; last write wins.
   */
  saveProvisionalForecastForNDC: (
    payload: SaveProvisionalForecastForNDCRequest
  ): Promise<SaveProvisionalForecastForNDCResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<SaveProvisionalForecastForNDCResponse>(
          'fdp/save_provisional_forecast_for_ndc',
          payload
        );
        resolve(data);
      } catch (error) {
        console.log('in services > saveProvisionalForecastForNDC, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * POST /fdp/get_provisional_forecast_for_ndcs
   *
   * Multi-NDC fetch. Body `{ndcs: [...]}`; all NDCs must resolve and
   * share one GFC. Response shares a single `market_data` envelope and
   * carries one `provisional_data` block per NDC (in input order, after
   * dedupe).
   *
   * Use this when an Edit screen has several NDC tabs to load — a
   * single round-trip beats N parallel single-NDC calls and the
   * response is already shape-aligned for tab-by-tab rendering.
   */
  getProvisionalForecastForNDCs: (
    payload: GetProvisionalForecastForNDCsRequest
  ): Promise<GetProvisionalForecastForNDCsResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<GetProvisionalForecastForNDCsResponse>(
          'fdp/get_provisional_forecast_for_ndcs',
          payload
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getProvisionalForecastForNDCs, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * POST /fdp/save_provisional_forecast_for_ndcs
   *
   * Multi-NDC save, atomic. Body `{items: [<single body>...]}`. The
   * backend pre-flight-validates every item; if any item fails, NOTHING
   * is written. On full validation success every item persists inside
   * one outer transaction so a mid-batch DB error also rolls back the
   * whole save.
   *
   * On 4xx the response carries a per-item `errors` array — the caller
   * is expected to surface those errors back into their respective tabs
   * rather than show a single global toast.
   */
  saveProvisionalForecastForNDCs: (
    payload: SaveProvisionalForecastForNDCsRequest
  ): Promise<SaveProvisionalForecastForNDCsResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<SaveProvisionalForecastForNDCsResponse>(
          'fdp/save_provisional_forecast_for_ndcs',
          payload
        );
        resolve(data);
      } catch (error) {
        console.log('in services > saveProvisionalForecastForNDCs, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * POST /fdp/get_ndc11_notes_for_ndcs
   *
   * Bulk fetch of active notes for many NDC11s scoped to one PlanType.
   * One round-trip on Edit mount seeds the per-tab notes for every NDC
   * in the batch.
   *
   * Response shape: `{notes_by_ndc: {ndc: [notes...]}}` — every
   * requested NDC has a key (empty array if no notes), so the caller
   * can populate its map without a follow-up presence check.
   */
  getNDC11NotesForNDCs: (
    payload: GetNDC11NotesForNDCsRequest
  ): Promise<GetNDC11NotesForNDCsResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<GetNDC11NotesForNDCsResponse>(
          'fdp/get_ndc11_notes_for_ndcs',
          payload
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getNDC11NotesForNDCs, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * POST /fdp/save_ndc11_note
   *
   * Creates one note scoped to (NDC11, PlanType). Returns the full
   * post-insert active note list for that NDC — caller can use it to
   * replace its local cache without an extra GET.
   */
  saveNDC11Note: (payload: SaveNDC11NoteRequest): Promise<SaveNDC11NoteResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<{
          msg: string;
          data: notesInterface[];
        }>('fdp/save_ndc11_note', payload);
        resolve({ msg: data.msg, data: data.data });
      } catch (error) {
        console.log('in services > saveNDC11Note, Err===', error);
        reject(error);
      }
    });
  },
};
